import {useState} from 'react';

import {useDeleteCertificateMutation} from './useCertificateMutations';
import {useCertificatesSelection} from './useCertificatesSelection';
import {
    Certificate,
    DeleteCertificateResult,
} from '../types';

// FNP-1193 — the delete flow: request, confirm, and hold the provider's answer.
//
// ⚠️ THIS HOOK DIFFERS FROM EVERY OTHER DELETE FLOW IN THE APP, and the reason
// is the whole point of `DeleteCertificateResult`.
//
// Elsewhere a delete either succeeds or throws, so the modal closes on success
// and renders the thrown error otherwise. Here the provider can REFUSE, and a
// refusal arrives through the SUCCESS path as a resolved result with
// `deleted: false`.
//
// So this hook keeps the RESULT, not just an error. The modal closes only when
// `deleted` is true; a refusal keeps it open and renders the reason, because
// "this certificate is serving something" is exactly the message an operator
// needs to read and a toast would take it away mid-sentence.
export const useCertificateDeletion = () => {

    const [certificate, setCertificate] = useState<Certificate | undefined>(undefined);
    const [opened, setOpened] = useState(false);
    // ⚠️ THE PROVIDER'S REFUSAL, HELD FOR THE MODAL. Not an error — a resolved
    // answer that happens to say no.
    const [refusal, setRefusal] = useState<DeleteCertificateResult | undefined>(undefined);

    const {clearSelection} = useCertificatesSelection();
    const deleteMutation = useDeleteCertificateMutation();

    const requestDelete = (next: Certificate) => {
        // The previous attempt's refusal must not greet the next confirmation.
        deleteMutation.reset();
        setRefusal(undefined);
        setCertificate(next);
        setOpened(true);
    };

    const cancelDelete = () => {
        setOpened(false);
        setCertificate(undefined);
        setRefusal(undefined);
        deleteMutation.reset();
    };

    const confirmDelete = () => {
        if (!certificate) {
            return;
        }
        deleteMutation.mutate({id: certificate.id}, {
            onSuccess: (result) => {

                // ⚠️ A RESOLVED MUTATION IS NOT A COMPLETED DELETE. The
                // provider is the authority and this is where its answer is
                // read — never before the call, and never by consulting local
                // state.
                if (result && !result.deleted) {
                    setRefusal(result);
                    // Modal stays OPEN. The certificate is still there, still
                    // serving something, and still selected.
                    return;
                }

                setOpened(false);
                setCertificate(undefined);
                setRefusal(undefined);
                clearSelection();
            },
        });
    };

    return {
        certificate,
        opened,
        refusal,
        requestDelete,
        cancelDelete,
        confirmDelete,
        isDeleting: deleteMutation.isPending,
        // A genuine transport or authorisation failure. Distinct from `refusal`
        // — one is the provider answering, the other is not reaching it.
        error: deleteMutation.error,
    };
};
